import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { SearchHit } from "@rakazo/contracts";
import { searchHitListKey, searchSnippetSegments } from "@rakazo/core";

export function SpaceSearchResults({
  hits,
  query,
  loading,
  onSelect,
}: {
  hits: SearchHit[];
  query: string;
  loading: boolean;
  onSelect: (hit: SearchHit) => void;
}) {
  if (loading) {
    return (
      <div className="px-3 py-4 text-[14px] text-muted-foreground">
        <Trans>Searching…</Trans>
      </div>
    );
  }
  if (!hits.length) {
    return (
      <div className="px-3 py-4 text-[14px] text-muted-foreground">
        <Trans>No results</Trans>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      {hits.map((hit) => (
        <button
          key={searchHitListKey(hit)}
          type="button"
          onClick={() => onSelect(hit)}
          className="rounded-xl px-2.5 py-[11px] text-start hover:bg-sidebar-accent"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[15px] font-medium text-foreground" dir="auto">
              {hit.title}
            </span>
            <span className="shrink-0 text-[12px] uppercase tracking-wide text-muted-foreground/80">
              {searchKindLabel(hit)}
            </span>
          </div>
          <div className="mt-0.5 flex min-w-0 gap-2 text-[13px] text-muted-foreground" dir="auto">
            <span className="max-w-[40%] shrink-0 truncate text-foreground/75">
              {hit.groupName ?? hit.botName}
            </span>
            <span data-testid="search-snippet" className="truncate">
              <SearchSnippet snippet={hit.snippet} query={query} />
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

/** The snippet with the searched words marked; text nodes only, so it can never inject markup. */
export function SearchSnippet({ snippet, query }: { snippet: string; query: string }) {
  return searchSnippetSegments(snippet, query).map((segment, index) =>
    segment.match ? (
      <mark key={index} className="rounded-sm bg-primary/15 text-foreground">
        {segment.text}
      </mark>
    ) : (
      segment.text
    ),
  );
}

function searchKindLabel(hit: SearchHit): string {
  switch (hit.kind) {
    case "conversation":
      return hit.groupId ? t`Group` : t`Nexa Mate`;
    case "message":
      return t`Message`;
    case "file":
      return t`File`;
    case "link":
      return t`Link`;
    case "routine":
      return t`Routine`;
  }
}
